import { useCallback, useRef, useState, useEffect } from "react";
import { Link as RouterLink } from "react-router";
import { motion } from "framer-motion";
import { Play, Pause, Heart, Copy, Link, MoreHorizontal, ExternalLink, Share2, QrCode } from "lucide-react";
import type { SoundResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { cn, formatPlayCount } from "@/lib/utils";
import { shareLink } from "@/lib/share";
import { QrModal } from "@/components/qr-modal";

interface SoundCardProps {
  sound: SoundResponse;
  index?: number;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onPlay?: () => void;
  compact?: boolean;
}

export function SoundCard({
  sound,
  index,
  isFavorite,
  onToggleFavorite,
  onPlay,
  compact,
}: SoundCardProps) {
  const { play, isPlaying } = useAudio();
  const playing = isPlaying(sound.id);
  const [showMenu, setShowMenu] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const preloadRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (preloadRef.current) {
        preloadRef.current.src = "";
        preloadRef.current = null;
      }
    };
  }, []);

  const handlePlay = useCallback(() => {
    play(sound.id, sound.audio_url, {
      title: sound.title,
      durationMs: sound.duration_ms,
      slug: sound.slug,
    });
    onPlay?.();
  }, [play, sound, onPlay]);

  const handleHover = useCallback(() => {
    if (!preloadRef.current) {
      const audio = new Audio();
      audio.preload = "auto";
      audio.src = sound.audio_url;
      preloadRef.current = audio;
    }
  }, [sound.audio_url]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(`${window.location.origin}/s/${sound.slug}`);
    setShowMenu(false);
  }, [sound.slug]);

  const handleCopyAudioLink = useCallback(() => {
    const ext = sound.mime_type?.includes("wav") ? "wav" : "mp3";
    navigator.clipboard.writeText(`${window.location.origin}/clip/${sound.slug}.${ext}`);
    setShowMenu(false);
  }, [sound.slug, sound.mime_type]);

  const handleShare = useCallback(() => {
    shareLink(`${window.location.origin}/s/${sound.slug}`, `${sound.title} · Wizard Sounds`);
    setShowMenu(false);
  }, [sound.slug, sound.title]);

  return (
    <motion.div
      className={cn(
        "group relative h-full bg-slate border border-mist rounded-xl sm:rounded-[18px] transition-all duration-200 ease-out",
        showMenu && "z-20",
        playing && "border-spark shadow-[0_12px_32px_-8px_rgba(255,182,73,0.18)]",
        !playing && "hover:border-spark hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-8px_rgba(255,182,73,0.18)]",
        compact ? "p-2 sm:p-3" : "p-2 sm:p-3 md:p-[18px]",
      )}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.12 }}
      onMouseEnter={handleHover}
      onContextMenu={(e) => {
        e.preventDefault();
        setShowMenu(!showMenu);
      }}
    >
      <button
        onClick={handlePlay}
        className="flex w-full items-start gap-2 pr-7 text-left sm:gap-3 sm:pr-8"
      >
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full transition-colors duration-150",
            "h-7 w-7 sm:h-10 sm:w-10",
            playing
              ? "bg-spark-soft"
              : "bg-spark hover:bg-spark-soft",
          )}
        >
          {playing ? (
            <Pause className="h-2.5 w-2.5 text-ink sm:h-[14px] sm:w-[14px]" />
          ) : (
            <Play className="h-2.5 w-2.5 text-ink ml-0.5 sm:h-[14px] sm:w-[14px]" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 sm:gap-2">
            {index !== undefined && (
              <span className="font-mono text-[10px] text-fog tracking-wider">
                {index + 1}
              </span>
            )}
            <h3
              className={cn(
                "truncate font-sans font-medium",
                compact ? "text-xs sm:text-sm" : "text-xs sm:text-[15px]",
              )}
            >
              {sound.title}
            </h3>
          </div>

          <div className="mt-0.5 font-mono text-[9px] text-fog tracking-wide sm:mt-1.5 sm:text-[10px] md:text-[11px]">
            {formatPlayCount(sound.play_count)} plays
          </div>
        </div>
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        className="absolute right-2 top-2 rounded-lg p-1 text-fog hover:bg-slate-raised sm:right-3 sm:top-3 sm:p-1.5 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
      >
        <MoreHorizontal className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute left-0 right-0 top-10 z-50 rounded-xl border border-mist bg-slate-raised p-1 shadow-lg sm:left-auto sm:right-3 sm:top-12 sm:w-48 sm:rounded-[18px]">
            {onToggleFavorite && (
              <button
                onClick={() => {
                  onToggleFavorite();
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
              >
                <Heart className={cn("h-4 w-4", isFavorite ? "fill-ember text-ember" : "text-fog")} />
                {isFavorite ? "Unfavorite" : "Favorite"}
              </button>
            )}
            <button
              onClick={handleShare}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <Share2 className="h-4 w-4 text-fog" /> Share
            </button>
            <button
              onClick={() => {
                setShowQr(true);
                setShowMenu(false);
              }}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <QrCode className="h-4 w-4 text-fog" /> QR code
            </button>
            <RouterLink
              to={`/s/${sound.slug}`}
              onClick={() => setShowMenu(false)}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <ExternalLink className="h-4 w-4 text-fog" /> View details
            </RouterLink>
            <button
              onClick={handleCopyLink}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <Link className="h-4 w-4 text-fog" /> Copy link
            </button>
            <button
              onClick={handleCopyAudioLink}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-parchment hover:bg-mist/50"
            >
              <Copy className="h-4 w-4 text-fog" /> Copy direct MP3
            </button>
          </div>
        </>
      )}
      {showQr && (
        <QrModal
          url={`${window.location.origin}/s/${sound.slug}`}
          title={sound.title}
          onClose={() => setShowQr(false)}
        />
      )}
    </motion.div>
  );
}
